const TUTORIAL_TEST_TOKEN_KEY = 'tutorial_test_token';
const VIEW_AS_TOKEN_KEY = 'view_as_token';
const VIEW_AS_USERNAME_KEY = 'view_as_username';
const ACCESS_TOKEN_KEY = 'access_token';
const ACTIVE_MODE_KEY = 'active_mode';
// Per-browser ephemeral token for the public landing Drug-Lab demo. Stored in
// localStorage so the anonymous run survives refresh. LOWEST auth precedence —
// it must never override a real login (see getAuthToken).
const DRUG_LAB_ANON_TOKEN_KEY = 'drug_lab_anon_token';

function readStorage(storage, key) {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch (_) {
    return null;
  }
}

function writeStorage(storage, key, value) {
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch (_) {
    // Ignore storage write failures.
  }
}

function removeStorage(storage, key) {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch (_) {
    // Ignore storage removal failures.
  }
}

function getSessionStorage() {
  return typeof window === 'undefined' ? null : window.sessionStorage;
}

function getLocalStorage() {
  return typeof window === 'undefined' ? null : window.localStorage;
}

// ── Anon-demo isolation (per-tab, in-memory) ─────────────────────────────────
// The /play/:slug share demo must run as a TRULY isolated anon session even when
// the visitor is a logged-in user (a valid access_token sits in SHARED
// localStorage). A new browser tab is a fresh JS context, so this module-level
// flag is naturally per-tab: it starts false in every other tab and CANNOT leak
// into the user's logged-in tabs (it never touches storage). When set,
// getAuthToken() returns ONLY the anon-demo token, so identity (checkAuth),
// the axios interceptor, sockets, SSE, and telemetry all see anon at once —
// without clearing the real access token (which would log the user out
// everywhere). App.jsx sets it synchronously at boot for /play paths; setting a
// real access token (a genuine sign-in/sign-up) releases it.
let _anonDemoOnly = false;

export function setAnonDemoOnly(value) {
  _anonDemoOnly = !!value;
}

export function isAnonDemoOnly() {
  return _anonDemoOnly;
}

export function getTutorialTestToken() {
  return readStorage(getSessionStorage(), TUTORIAL_TEST_TOKEN_KEY);
}

export function hasTutorialTestToken() {
  return !!getTutorialTestToken();
}

export function clearTutorialTestToken() {
  removeStorage(getSessionStorage(), TUTORIAL_TEST_TOKEN_KEY);
}

export function getAccessToken() {
  return readStorage(getLocalStorage(), ACCESS_TOKEN_KEY);
}

export function setAccessToken(token) {
  if (token) {
    // A real access token means the visitor authenticated for real (login /
    // signup, including signing up FROM the demo). Release any anon-demo
    // isolation so they see their actual account instead of the anon view.
    setAnonDemoOnly(false);
    writeStorage(getLocalStorage(), ACCESS_TOKEN_KEY, token);
    return;
  }
  removeStorage(getLocalStorage(), ACCESS_TOKEN_KEY);
}

export function clearAccessToken() {
  removeStorage(getLocalStorage(), ACCESS_TOKEN_KEY);
}

export function getViewAsToken() {
  return readStorage(getSessionStorage(), VIEW_AS_TOKEN_KEY);
}

export function setViewAsToken(token, username) {
  if (token) {
    writeStorage(getSessionStorage(), VIEW_AS_TOKEN_KEY, token);
    if (username) {
      writeStorage(getSessionStorage(), VIEW_AS_USERNAME_KEY, username);
    }
    return;
  }
  removeStorage(getSessionStorage(), VIEW_AS_TOKEN_KEY);
  removeStorage(getSessionStorage(), VIEW_AS_USERNAME_KEY);
}

export function getViewAsUsername() {
  return readStorage(getSessionStorage(), VIEW_AS_USERNAME_KEY);
}

export function isViewingAs() {
  return !!getViewAsToken();
}

export function getDrugLabAnonToken() {
  return readStorage(getLocalStorage(), DRUG_LAB_ANON_TOKEN_KEY);
}

export function setDrugLabAnonToken(token) {
  if (token) {
    writeStorage(getLocalStorage(), DRUG_LAB_ANON_TOKEN_KEY, token);
    return;
  }
  removeStorage(getLocalStorage(), DRUG_LAB_ANON_TOKEN_KEY);
}

export function clearDrugLabAnonToken() {
  removeStorage(getLocalStorage(), DRUG_LAB_ANON_TOKEN_KEY);
}

export function getAuthToken() {
  // Anon-demo isolation (per-tab): in a /play demo tab, send ONLY the anon-demo
  // token and ignore any logged-in access token, so the demo never rides the
  // visitor's real account.
  if (_anonDemoOnly) {
    return getDrugLabAnonToken();
  }
  // Precedence: tutorial-test > view-as > real access token > anon-demo (lowest,
  // so a logged-in user's access token always wins over a lingering anon token).
  return getTutorialTestToken() || getViewAsToken() || getAccessToken() || getDrugLabAnonToken();
}

export function isAnonDemoSession() {
  // True when the EFFECTIVE auth (getAuthToken precedence) is the anon-demo
  // token — i.e. the visitor is in the no-signup landing preview. A lingering
  // anon token under a real login does NOT count (access token outranks it).
  // Forced isolation (a /play demo tab) always counts as an anon session.
  if (_anonDemoOnly) {
    return true;
  }
  return !getTutorialTestToken() && !getViewAsToken() && !getAccessToken() && !!getDrugLabAnonToken();
}

export function clearActiveAuthToken() {
  if (hasTutorialTestToken()) {
    clearTutorialTestToken();
    return;
  }
  clearAccessToken();
}

export function getActiveMode() {
  return readStorage(getLocalStorage(), ACTIVE_MODE_KEY);
}

export function setStoredActiveMode(mode) {
  if (mode) {
    writeStorage(getLocalStorage(), ACTIVE_MODE_KEY, mode);
    return;
  }
  removeStorage(getLocalStorage(), ACTIVE_MODE_KEY);
}

export function clearActiveMode() {
  removeStorage(getLocalStorage(), ACTIVE_MODE_KEY);
}
